import { create } from "zustand";
import axios from "axios";
import { fetchMe, type Me, type MeCrm, type MeUser } from "./api";
import { useAuth } from "../auth/use-auth";

export type MeLoadStatus = "idle" | "loading" | "ready" | "error";

interface MeState {
  me: Me | null;
  status: MeLoadStatus;
  httpStatus: number | null;
  error: unknown;
  // Fetches /v1/me once and caches it. No-ops while a load is in flight.
  load: () => Promise<void>;
  reset: () => void;
}

export const useMeStore = create<MeState>((set, get) => ({
  me: null,
  status: "idle",
  httpStatus: null,
  error: null,
  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null, httpStatus: null });
    try {
      const me = await fetchMe();
      set({ me, status: "ready", error: null, httpStatus: null });
    } catch (error) {
      const httpStatus = axios.isAxiosError(error)
        ? (error.response?.status ?? null)
        : null;
      set({ error, status: "error", httpStatus });
    }
  },
  reset: () => set({ me: null, status: "idle", error: null, httpStatus: null }),
}));

export function useMeUser(): MeUser | null {
  return useMeStore((state) => state.me?.user ?? null);
}

export function useMeCrm(): MeCrm | null {
  return useMeStore((state) => state.me?.crm ?? null);
}

export interface Identity {
  name: string;
  email: string;
  org?: string;
}

// Display identity for the shell: the /v1/me user once loaded, falling back to
// the bearer token's claims until then (or if the call fails) so the avatar and
// menu are never blank. Both are the real signed-in user — no placeholders.
export function useIdentity(): Identity {
  const user = useMeUser();
  const crm = useMeCrm();
  const auth = useAuth();
  const name = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : (auth.user?.name ?? auth.user?.email ?? "");
  const email = user?.email ?? auth.user?.email ?? "";
  return { name, email, org: crm?.name };
}
